/** t331u B6: phone Settings › Closures & bank holidays (desktop backlogUi ClosuresSettings): list by year, add, remove, gov.uk import. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import {
  BANK_HOLIDAY_DIVISIONS, BANK_HOLIDAYS_NOTE, bankHolidaysText, closureAddedText, closureBody, closureCanRemove, closureDate, closureForm, closureInvalid, closureLine, closuresByYear, closuresRange,
  closuresWide, CLOSURES_DEDUCT_NOTE, CLOSURES_NOTE, type BankHolidayDivision, type ClosureDay, type ClosureForm,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWfContext, useWorkspaceId } from '@/lib/hooks';
import { useLocationsWith, usePermissions } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Select, Spinner, ask } from '@/components/ui';
import { useWfLookups } from '@/features/workforce/common';
import { today } from '@/lib/format';
import { BottomSheet } from '../BottomSheet';
import { F, saveBtn } from './PersonSettings';
import { listCard, note, WsAddRow, WsHead, WsRow, WsSwitch } from './WorkspaceSettings';

export function ClosuresPage() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const mine = usePermissions();
  const wide = closuresWide(mine.data);
  const { locations } = useWfLookups();
  const allowed = useLocationsWith('workforce.closures.manage');
  const buildings = wide || allowed === 'all' ? locations : locations.filter(l => allowed.includes(l.id));
  const { from, to } = closuresRange(today());
  const q = useQuery({ queryKey: ['wf', ws, 'closures', from, to], queryFn: () => api.wf.closures(ws!, { from, to }), enabled: Boolean(ws) });
  const [sheet, setSheet] = useState<'add' | 'import' | null>(null);
  const [busy, setBusy] = useState(false);
  const locName = (id: string) => locations.find(l => l.id === id)?.name;
  const run = async <T,>(fn: () => Promise<T>, ok: (r: T) => string) => {
    setBusy(true);
    try { const r = await fn(); toast(ok(r), 'success'); void queryClient.invalidateQueries({ queryKey: ['wf', ws] }); return true; }
    catch (e) { toast(errorMessage(e, 'Couldn’t save settings'), 'error'); return false; }
    finally { setBusy(false); }
  };
  const del = async (c: ClosureDay) => { if (await ask({ title: `Remove ${c.name} on ${closureDate(c.date)}?`, confirmLabel: 'Remove', danger: true })) void run(() => api.wf.deleteClosure(ws!, c.id), () => 'Closure removed'); };
  const groups = closuresByYear(q.data?.closures ?? []);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-closures">
      <p className={note}>{CLOSURES_NOTE}</p>
      <div className={`${listCard} mt-3`} data-testid="m-closures-actions">
        <WsAddRow label="Add a closure" onClick={() => setSheet('add')} testId="m-closures-add" />
        {wide && <button type="button" onClick={() => setSheet('import')} data-testid="m-closures-import" className="flex min-h-[48px] w-full items-center gap-3 border-t border-slate-100 px-3 text-left text-base font-medium active:bg-slate-100 dark:border-slate-800 dark:active:bg-slate-800"><Download className="h-5 w-5" aria-hidden />Import UK bank holidays</button>}
      </div>
      {q.isLoading ? <div className="p-4"><Spinner /></div> : !groups.length ? <p className={note} data-testid="m-closures-empty">No closures this year or next.</p> : groups.map(g => (
        <section key={g.year}>
          <WsHead>{g.year}</WsHead>
          <div className={listCard} data-testid="m-closures-list">
            {g.items.map(c => (
              <WsRow key={c.id} testId="m-closures-row" title={c.name} line={closureLine(c, locName)}
                right={closureCanRemove(c, wide, allowed) ? <button type="button" className="flex h-11 w-11 shrink-0 items-center justify-center text-slate-500 disabled:opacity-30" aria-label={`Remove ${c.name}`} disabled={busy} onClick={() => void del(c)} data-testid="m-closures-remove"><Trash2 className="h-5 w-5" /></button> : undefined} />
            ))}
          </div>
        </section>
      ))}
      <p className={note}>{CLOSURES_DEDUCT_NOTE}</p>
      {sheet === 'add' && <AddSheet wide={wide} buildings={buildings} busy={busy} onClose={() => setSheet(null)}
        onSave={f => run(() => api.wf.createClosure(ws!, closureBody(f)), r => closureAddedText(r.existing_shifts))} />}
      {sheet === 'import' && <ImportSheet busy={busy} onClose={() => setSheet(null)}
        onImport={(division, deducts) => run(() => api.wf.importBankHolidays(ws!, { division, deducts_entitlement: deducts }), bankHolidaysText)} />}
    </div>
  );
}

function AddSheet({ wide, buildings, busy, onClose, onSave }: { wide: boolean; buildings: { id: string; name: string }[]; busy: boolean; onClose: () => void; onSave: (f: ClosureForm) => Promise<boolean> }) {
  const [f, setF] = useState<ClosureForm>(() => closureForm(wide ? '' : buildings[0]?.id ?? ''));
  return (
    <BottomSheet open onClose={onClose} title="Add a closure" testId="m-closures-sheet"
      footer={<div className="flex px-3 py-2"><button type="button" className={saveBtn} disabled={busy || closureInvalid(f, wide)} onClick={async () => { if (await onSave(f)) onClose(); }} data-testid="m-closures-save">Add closure</button></div>}>
      <div className="space-y-1 px-4 pb-2">
        <F label="Date"><Input width="full" type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="m-closures-date" /></F>
        <F label="Name"><Input placeholder="e.g. Stocktake – closed" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="m-closures-name" /></F>
        <F label="Applies to"><Select value={f.location_id} onChange={e => setF({ ...f, location_id: e.target.value })} data-testid="m-closures-building">
          {wide && <option value="">All buildings</option>}{buildings.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></F>
        <WsSwitch label="Deducts leave" checked={f.deducts_entitlement} onChange={v => setF({ ...f, deducts_entitlement: v })} testId="m-closures-deducts" />
      </div>
    </BottomSheet>
  );
}

function ImportSheet({ busy, onClose, onImport }: { busy: boolean; onClose: () => void; onImport: (d: BankHolidayDivision, deducts: boolean) => Promise<boolean> }) {
  const wf = useWfContext();
  const [division, setDivision] = useState<BankHolidayDivision>(wf.data?.settings.bank_holiday_division ?? 'england-and-wales');
  const [deducts, setDeducts] = useState(false);
  return (
    <BottomSheet open onClose={onClose} title="Import UK bank holidays" testId="m-closures-import-sheet"
      footer={<div className="flex px-3 py-2"><button type="button" className={saveBtn} disabled={busy} onClick={async () => { if (await onImport(division, deducts)) onClose(); }} data-testid="m-closures-import-go">Import this year and next</button></div>}>
      <div className="space-y-1 px-4 pb-2">
        <F label="Division"><Select value={division} onChange={e => setDivision(e.target.value as BankHolidayDivision)} data-testid="m-closures-division">
          {BANK_HOLIDAY_DIVISIONS.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></F>
        <WsSwitch label="Deduct from holiday entitlement" checked={deducts} onChange={setDeducts} testId="m-closures-import-deducts" />
        <p className="pt-1 text-sm text-slate-500">{BANK_HOLIDAYS_NOTE}</p>
      </div>
    </BottomSheet>
  );
}
